// src/components/CheckinSignoffModal.jsx
//
// Modal for clearing (signing off), undoing, or marking N/A a single check-in
// item on a student's record. The check-in analogue of CheckoutSignoffModal,
// minus the multi-teacher slots and the OSL early-signoff override logic
// (check-in items have no such chain).
//
// Modes:
//   'sign' : record a clearance (notes optional)
//   'undo' : revert a clearance (reason optional)
//   'na'   : superadmin-only — mark not applicable to this student
//
// Calls the signoffCheckinItem callable; the server re-verifies that the
// caller is authorized for this item (so this UI gate is convenience, not
// security).

import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { KA_COLORS } from '../utils/checkinHelpers';

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0, 0, 0, 0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 16,
  },
  modal: {
    background: '#fff', borderRadius: 8,
    width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  header: (mode) => ({
    background: mode === 'undo' ? '#777' : KA_COLORS.navy,
    color: '#fff',
    padding: '16px 20px', borderRadius: '8px 8px 0 0',
  }),
  headerTitle: { margin: 0, fontSize: 18, fontWeight: 700 },
  headerSubtitle: { margin: '4px 0 0', fontSize: 13, opacity: 0.85 },
  body: { padding: 20 },
  studentBlock: {
    background: KA_COLORS.lightGray, borderRadius: 6, padding: 12, marginBottom: 16,
  },
  studentName: { fontSize: 14, fontWeight: 600, color: KA_COLORS.navy, margin: 0 },
  studentMeta: { fontSize: 12, color: '#666', margin: '4px 0 0' },
  itemLabel: {
    fontSize: 13, fontWeight: 700, color: KA_COLORS.navy,
    textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4,
  },
  itemTitle: { fontSize: 16, fontWeight: 600, color: '#222', margin: '0 0 4px' },
  itemDesc: { fontSize: 13, color: '#555', margin: 0, lineHeight: 1.4 },
  deptLine: { fontSize: 12, color: '#888', margin: '6px 0 0' },
  divider: {
    border: 'none', borderTop: `1px solid ${KA_COLORS.borderGray}`, margin: '16px 0',
  },
  fieldLabel: {
    display: 'block', fontSize: 13, fontWeight: 600, color: '#333', marginBottom: 6,
  },
  textarea: {
    width: '100%', padding: '8px 10px',
    border: `1px solid ${KA_COLORS.borderGray}`, borderRadius: 4,
    fontSize: 13, minHeight: 80, fontFamily: 'inherit',
    resize: 'vertical', boxSizing: 'border-box',
  },
  prevSignoff: {
    padding: 10, marginBottom: 12,
    background: '#E8F5E9', border: '1px solid #C8E6C9',
    borderRadius: 4, fontSize: 13, color: '#2E7D32',
  },
  errorBox: {
    marginTop: 12, padding: 10,
    background: '#FFEBEE', border: '1px solid #FFCDD2',
    borderRadius: 4, color: '#C62828', fontSize: 13,
  },
  footer: {
    padding: '14px 20px', borderTop: `1px solid ${KA_COLORS.borderGray}`,
    display: 'flex', justifyContent: 'flex-end', gap: 10,
  },
  cancelBtn: {
    padding: '8px 16px', border: `1px solid ${KA_COLORS.borderGray}`,
    background: '#fff', borderRadius: 4, cursor: 'pointer', fontSize: 14, color: '#333',
  },
  confirmBtn: (disabled, mode) => ({
    padding: '8px 16px', border: 'none',
    background: disabled ? '#ccc' : mode === 'undo' ? '#777' : KA_COLORS.red,
    color: '#fff', borderRadius: 4,
    cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 14, fontWeight: 600,
  }),
};

const CheckinSignoffModal = ({
  record,
  item,            // resolved check-in item ({ id, label, description, department, ... })
  user,            // current auth user
  mode = 'sign',   // 'sign' | 'undo' | 'na'
  onClose,
  onSuccess,
}) => {
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  if (!record || !item) return null;

  const itemData = record.items?.[item.id] || {};

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const functions = getFunctions();
      const signoffCheckinItem = httpsCallable(functions, 'signoffCheckinItem');
      await signoffCheckinItem({
        recordId: record.id || `${record.schoolYear}_${record.studentId}`,
        itemId: item.id,
        action: mode, // 'sign' | 'undo' | 'na'
        notes: mode === 'sign' ? notes.trim() : '',
        reason: mode === 'undo' ? reason.trim() : '',
      });
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('signoffCheckinItem failed:', err);
      setError(err?.message || 'The action failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !submitting) onClose();
  };

  const titleByMode = {
    sign: 'Clear Item',
    undo: 'Undo Clearance',
    na: 'Mark Item N/A',
  };
  const subtitleByMode = {
    sign: `Confirm "${item.label}" is complete for this student.`,
    undo: 'Reverting a prior clearance. Anyone authorized for this item can do this; the action is logged.',
    na: 'Mark this item as not applicable to this student. Superadmin only.',
  };
  const verbByMode = { sign: 'Clear item', undo: 'Confirm undo', na: 'Mark N/A' };

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal} role="dialog" aria-modal="true">
        <div style={styles.header(mode)}>
          <h2 style={styles.headerTitle}>{titleByMode[mode]}</h2>
          <p style={styles.headerSubtitle}>{subtitleByMode[mode]}</p>
        </div>

        <div style={styles.body}>
          <div style={styles.studentBlock}>
            <p style={styles.studentName}>{record.studentName || 'Unknown'}</p>
            <p style={styles.studentMeta}>
              {[record.grade, record.cohort, record.studentId && `SIS ${record.studentId}`]
                .filter(Boolean)
                .join('  •  ')}
            </p>
          </div>

          <div style={styles.itemLabel}>Item</div>
          <h3 style={styles.itemTitle}>{item.label}</h3>
          {item.description && <p style={styles.itemDesc}>{item.description}</p>}
          {item.department && (
            <p style={styles.deptLine}>Contact: <strong>{item.department}</strong></p>
          )}

          <hr style={styles.divider} />

          {mode === 'undo' && itemData.signedByName && (
            <div style={styles.prevSignoff}>
              Currently cleared by <strong>{itemData.signedByName}</strong>.
              {itemData.notes && (
                <>
                  <br />
                  <em>Notes:</em> {itemData.notes}
                </>
              )}
            </div>
          )}

          {mode === 'sign' && (
            <>
              <label style={styles.fieldLabel} htmlFor="checkin-notes">
                Notes (optional)
              </label>
              <textarea
                id="checkin-notes"
                style={styles.textarea}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., Form received and filed."
                disabled={submitting}
              />
            </>
          )}
          {mode === 'undo' && (
            <>
              <label style={styles.fieldLabel} htmlFor="checkin-undo-reason">
                Reason for undo (optional)
              </label>
              <textarea
                id="checkin-undo-reason"
                style={styles.textarea}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Cleared by mistake — form still missing a signature."
                disabled={submitting}
              />
            </>
          )}

          {error && <div style={styles.errorBox}>{error}</div>}
        </div>

        <div style={styles.footer}>
          <button type="button" style={styles.cancelBtn} onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            style={styles.confirmBtn(submitting, mode)}
            onClick={handleConfirm}
            disabled={submitting}
          >
            {submitting ? 'Working…' : verbByMode[mode]}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckinSignoffModal;